// File: e1c/src/components/content_editor/QuizOpenTextBlock.js
// Description: A dedicated block for creating quiz questions (open text)
// with media, and a model answer/explanation.

import React, { useCallback, useMemo } from 'react';
import { Paper, TextField, IconButton, Grid, Typography, Box } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MediaAttachmentManager from './MediaAttachmentManager';
import PatientQuestionRenderer from './PatientQuestionRenderer'; 
// --- Local helper to generate option values ---
const generateValueForChoiceOption = () =>
  `val_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;

const QuizOpenTextBlock = React.memo(({ id, question, options = {}, onChange, onDelete }) => {

    const memoizedOptions = useMemo(() => ({
        placeholder: options.placeholder || '',
        rows: options.rows ?? 3,
        media_assets: options.media_assets || [],
        model_answer: options.model_answer || '' // <-- NEW: For feedback
    }), [options]);

    const handleQuestionChange = useCallback((event) => {
        if (onChange) { onChange({ question: event.target.value }); }
    }, [onChange]);

    const handleOptionChange = useCallback((optionName, value) => {
        if (onChange) {
            onChange({ options: { [optionName]: value } });
        }
    }, [onChange]);
    
    // Handle media changes separately
    const handleMediaChange = useCallback((newMedia) => {
        if (onChange) {
            onChange({ options: { media_assets: newMedia.media_assets } });
        }
    }, [onChange]);


    const handleDeleteClick = useCallback(() => {
        if (onDelete) { onDelete(id); }
    }, [id, onDelete]);

    const previewDefinition = useMemo(() => ({
        id,
        question,
        type: 'open_text', // This preview will still render as a normal open_text
        options: memoizedOptions
    }), [id, question, memoizedOptions]);

    return (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, position: 'relative', backgroundColor: 'grey.50', '&:hover .delete-button': { opacity: 1 } }}>
            <IconButton
                className="delete-button" aria-label="delete block" onClick={handleDeleteClick} size="small"
                sx={{ position: 'absolute', top: 8, right: 8, color: 'grey.500', opacity: 0.5, transition: 'opacity 0.2s', '&:hover': { opacity: 1, color: 'error.main' } }}
            >
                <DeleteOutlineIcon fontSize="small"/>
            </IconButton>
            
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main', fontWeight: 'bold' }}>
                Quiz Question (Open Text)
            </Typography>

            <TextField
                fullWidth variant="outlined" size="small"
                label={`Quiz Question Text (ID: ${id})`}
                multiline minRows={1} value={question || ''}
                onChange={handleQuestionChange}
                placeholder="Enter the quiz question..."
                sx={{ mb: 2 }}
            />

            {/* --- MEDIA MANAGER --- */}
            <MediaAttachmentManager
                mediaAssets={memoizedOptions.media_assets}
                onChange={handleMediaChange}
            />
            {/* --- END MEDIA MANAGER --- */}

            <Grid container spacing={2} sx={{mt: 0.5}}>
                <Grid item xs={12} sm={8}>
                    <TextField
                        fullWidth variant="outlined" size="small"
                        label="Placeholder Text (for patient input)"
                        value={memoizedOptions.placeholder}
                        onChange={(e) => handleOptionChange('placeholder', e.target.value)}
                        placeholder="e.g., Type your thoughts here..."
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField
                        fullWidth type="number" variant="outlined" size="small" label="Input Rows"
                        value={memoizedOptions.rows ?? ''}
                        onChange={(e) => handleOptionChange('rows', e.target.value === '' ? 3 : parseInt(e.target.value, 10))}
                        inputProps={{ min: 1, step: 1 }}
                        helperText="Lines for patient's answer"
                    />
                </Grid>
            </Grid>
            
            {/* --- NEW: MODEL ANSWER FIELD --- */}
            <TextField
                fullWidth variant="filled" size="small"
                label="Model Answer / Feedback"
                multiline minRows={2}
                value={memoizedOptions.model_answer}
                onChange={(e) => handleOptionChange('model_answer', e.target.value)}
                placeholder="Enter the explanation or model answer to show the patient after they respond."
                sx={{ mt: 2, backgroundColor: 'success.lightest' }}
            />
            {/* --- END MODEL ANSWER --- */}

            <Box sx={{ mt: 2, p: 1.5, border: '1px dashed lightgrey', borderRadius: 1 }}>
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 1 }}>
                    Patient View Preview (Input Only):
                </Typography>
                {/* This uses the standard renderer, which is fine for previewing the INPUT part */}
                <PatientQuestionRenderer questionDefinition={previewDefinition} />
            </Box>
        </Paper>
    );
});

export default QuizOpenTextBlock;